import React from 'react';
import { Box, Container, Typography, Grid, IconButton, Link, alpha, useTheme, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';

const Footer = ({ scrollToSection }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    [t('product')]: [
      { label: t('features'), id: 'features' },
      { label: t('pricing'), id: 'pricing' },
      { label: t('for_whom'), id: 'for-whom' },
    ],
    [t('support')]: [
      { label: t('contact'), id: 'contact' },
      { label: t('request_demo'), id: 'demo' },
    ],
    [t('company')]: [
      { label: t('about'), id: 'about' },
      { label: t('contact'), id: 'contact' },
    ],
    [t('legal')]: [
      { label: t('privacy_policy'), href: '/privacy-policy' },
      { label: t('terms_of_service'), href: '/terms-of-service' },
    ],
  };

  const socialLinks = [
    { icon: <FacebookIcon />, href: '#', label: 'Facebook' },
    { icon: <TwitterIcon />, href: '#', label: 'Twitter' },
    { icon: <LinkedInIcon />, href: '#', label: 'LinkedIn' },
    { icon: <InstagramIcon />, href: '#', label: 'Instagram' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#1a1a2e',
        color: '#fff',
        pt: 8,
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ReceiptLongIcon sx={{ fontSize: 32, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Billeur
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 3, color: alpha('#fff', 0.7), lineHeight: 1.7 }}>
              {t('footer_description')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((social, index) => (
                <IconButton
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  sx={{
                    color: '#fff',
                    backgroundColor: alpha('#fff', 0.1),
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {Object.entries(footerLinks).map(([category, links], index) => (
            <Grid item xs={6} sm={3} md={2} key={index}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
                {category}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {links.map((link, idx) => (
                  link.id ? (
                    <Link
                      key={idx}
                      component="button"
                      onClick={() => scrollToSection(link.id)}
                      sx={{
                        color: alpha('#fff', 0.7),
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        textAlign: 'left',
                        '&:hover': {
                          color: '#fff',
                          textDecoration: 'none',
                        },
                        transition: 'color 0.2s',
                      }}
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <Link
                      key={idx}
                      component="button"
                      onClick={() => navigate(link.href)}
                      sx={{
                        color: alpha('#fff', 0.7),
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        textAlign: 'left',
                        '&:hover': {
                          color: '#fff',
                          textDecoration: 'none',
                        },
                        transition: 'color 0.2s',
                      }}
                    >
                      {link.label}
                    </Link>
                  )
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4, borderColor: alpha('#fff', 0.1) }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
            {t('footer_copyright', { year: currentYear })}
          </Typography>
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
            {t('made_in_belgium')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
